import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance as CxpIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  PictureAsPdf as NcfIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  LocalShipping as RecepcionIcon,
  AssignmentTurnedIn as LiquidacionIcon,
  SavingsOutlined as AvancesIcon,
  ReceiptLongOutlined as NotasCreditoSuplidorIcon,
  Close as CloseIcon,
  GridOn as Formato606Icon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { FP, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import { cuentasPosteablesDeCatalogo } from './entradaDiarioShared';
import Formato606 from './DialogoFormato606';

const PROGRAMA = 'registrarcuentasxpagar';
const PROGRAMA_MANTENIMIENTO = 'mantenimientocuentasxpagar';
const TIPO_DOCUMENTO_MOTOR_CXP = 'Cuentas por Pagar';

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === 'false') return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || '').trim().toLowerCase() === 'contabilidad');
};

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  if (!tipo) return false;
  return (registros || []).some((r) => {
    if (String(r?.tipo_documento || '').trim() !== tipo) return false;
    const aplica = String(r?.aplica_a || 'cuenta').trim().toLowerCase();
    if (aplica === 'json') return true;
    return Boolean(String(r?.cuentacontable || '').trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  console.log('registros', registros);
  const tipo = String(tipoDocumento || '').trim();
  return (registros || []).filter((r) => String(r?.tipo_documento || '').trim() === tipo);
};

const CLAVES_META_MOTOR = new Set([
  'tipodocumento',
  'nfactura',
  'documento',
  'numero',
  'suplidor',
  'fecha',
  'tasa',
  'usuario',
  'concepto',
  'valor',
  'valor_total',
  'lineas',
  'cuentas',
]);

const lineasCuentasJsonMotor = (lineas) =>
  (lineas || [])
    .map((ln) => ({
      cuenta: String(ln?.cuenta || '').trim(),
      monto: redondearDinero2(parseFloat(ln?.valor) || 0),
      localidad: String(ln?.localidad || '').trim(),
    }))
    .filter((x) => x.cuenta);

/** El motor indica por regla si el valor es un monto (cuenta fija) o un JSON de cuentas. */
const construirDocumentoMotorCxp = (reglas, ctx) => {
  const cuentasJson = lineasCuentasJsonMotor(ctx.lineas);
  const doc = {
    tipodocumento: TIPO_DOCUMENTO_MOTOR_CXP,
    nfactura: ctx.nfactura,
    documento: ctx.documento,
    numero: ctx.numero,
    suplidor: ctx.suplidor,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor: ctx.valorTotal,
    lineas: cuentasJson,
  };
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || '').trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const aplica = String(regla?.aplica_a || 'cuenta').trim().toLowerCase();
    doc[codigo] = aplica === 'json' ? cuentasJson : ctx.valorTotal;
  });
  return doc;
};

/** Pagos en efectivo/cheque (vplicado) + notas de crédito aplicadas a la factura (ncpagada). */
function pagadoCxPDesglose(row) {
  const vp = parseFloat(row?.vplicado) || 0;
  const nc = parseFloat(row?.ncpagada) || 0;
  return vp + nc;
}

const FORMAS_PAGO_DOCUMENTO = ['Contado', 'Credito'];
const OPCIONES_EXCENTO = ['No', 'Si'];
const TIPOS_ID_NCF = ['1-Rnc', '2-Cedula'];
const TIPOS_SERVICIO_NCF = [
  '00-NONE',
  '01-GASTOS DE PERSONAL',
  '02-GASTOS POR TRABAJOS SUMINISTROS Y SERVICIOS',
  '03-ARRENDAMIENTOS',
  '04-GASTOS DE ACTIVO FIJOS',
  '05-GASTOS DE REPRESENTACION',
  '06-OTRAS DEDUCCIONES ADMITIDAS',
  '07-GASTOS FINANCIEROS',
  '08-GASTOS EXTRAORDINARIOS',
  '09-COMPRAS Y GASTOS QUE FORMARAN PARTE DEL COSTO DE VENTA',
  '10-ADQUISICIONES DE ACTIVOS',
  '11-ADQUISICIONES DE ACTIVOS',
  '12-GASTOS DE SEGUROS',
];
const TIPOS_BIEN_NCF = ['00-None', '01-Servicios', '02-Bienes'];
const TIPOS_RETENCION_NCF = [
  '00-None',
  '01-Alquileres',
  '02-Honorarios Profesionales',
  '03-Otras Rentas',
  '04-Otras Rentas (Rentas Presuntas)',
  '05-Interes Pagados a Personas Juridicas',
  '06-Interes Pagados a Personas Fisicas Residentes',
  '07-Retenciones Provedores de Estado',
  '08-Otras Rentas (Rentas Presuntas)',
  '09-Juegos Telefonicos',
];
const FORMAS_PAGO_NCF = [
  '00-NONE',
  '01-EFECTIVO',
  '02-CHEQUE/TRANSFERENCIA',
  '03-TARJETA DE CREDITO',
  '04-COMPRA CREDITO',
  '05-PERMUTA',
  '06-NOTA DE CREDITO',
  '07-MIXTO',
];

const NcfSeccion = ({ titulo, descripcion, children }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 2.5,
      border: `1px solid ${FP.color.border}`,
      bgcolor: FP.color.cardBg,
      boxShadow: FP.shadow.soft,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        px: { xs: 2, md: 2.5 },
        py: 1.45,
        borderBottom: `1px solid ${FP.color.border}`,
        bgcolor: '#FAFBFE',
      }}
    >
      <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.92rem', letterSpacing: '-0.01em' }}>
        {titulo}
      </Typography>
      {descripcion ? (
        <Typography sx={{ color: FP.color.textMuted, fontSize: '0.75rem', mt: 0.3 }}>
          {descripcion}
        </Typography>
      ) : null}
    </Box>
    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>{children}</CardContent>
  </Card>
);

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Redondeo a 2 decimales (misma lógica que el backend para avances vs total factura). */
const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const tramoLabel = (t) => {
  if (t === '90+') return 'Más de 90 días';
  return `${t} días`;
};

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const labelCuentaCatalogo = (c) => {
  if (!c) return '';
  const cod = String(c.cuenta ?? '').trim();
  const nom = String(c.nombre ?? '').trim();
  return cod && nom ? `${cod}-${nom}` : cod || nom;
};

const labelCentroCosto = (c) => {
  if (!c) return '';
  const id = c.id != null ? String(c.id).trim() : '';
  const d = String(c.descrip ?? '').trim();
  return id && d ? `${id}-${d}` : id || d;
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** "M202403" o "202403" → YYYYMM (login / varinicial) */
const wanomesDesdeAnomesContabilidad = (anomes) => {
  const s = String(anomes ?? '').trim();
  const digits = s.startsWith('M') || s.startsWith('m') ? s.slice(1) : s;
  if (!/^\d{6}$/.test(digits)) return null;
  return digits;
};

const zanomesDesdeFechaStr = (fechaStr) => {
  const t = (fechaStr || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const [y, m] = t.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || m < 1 || m > 12) return null;
  return `${y}${String(m).padStart(2, '0')}`;
};

/** Si YYYYMM de la fecha < YYYYMM de varinicial → mes cerrado */
const validarMesContableAbierto = (fechaStr, wanomesAbierto) => {
  if (!wanomesAbierto) {
    return { ok: true };
  }
  const z = zanomesDesdeFechaStr(fechaStr);
  if (!z) {
    return { ok: false, message: 'Indique una fecha de transacción válida.' };
  }
  if (parseInt(z, 10) < parseInt(wanomesAbierto, 10)) {
    return {
      ok: false,
      message:
        'La fecha se encuentra en un mes cerrado (anterior al período contable abierto en varinicial: anoinicialcont / mesinicialcont).',
    };
  }
  return { ok: true };
};

const RegistrarCuentasXPagar = ({ open = false, onClose, onGuardado }) => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles, modulos } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer =
    tienePermisosModulo(roleDetalles, PROGRAMA) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_MANTENIMIENTO);
  const puedeModificar =
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA) ||
    puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_MANTENIMIENTO);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);
  const usaCuentasContables = moduloContabilidadInstalado(modulos);

  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardandoCxp, setGuardandoCxp] = useState(false);
  const [fechaTransaccion, setFechaTransaccion] = useState(() => fechaLocalHoy());
  const [fechaCorteAntiguedad, setFechaCorteAntiguedad] = useState(null);
  const [wanomesContableAbierto, setWanomesContableAbierto] = useState(null);

  const [cuentasCatalogo, setCuentasCatalogo] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  const [cargandoCentros, setCargandoCentros] = useState(false);
  const [cuentaLineaSel, setCuentaLineaSel] = useState(null);
  const [descripcionLineaStr, setDescripcionLineaStr] = useState('');
  const [centroLineaSel, setCentroLineaSel] = useState(null);
  const [valorLineaStr, setValorLineaStr] = useState('');
  const [lineasDistribucion, setLineasDistribucion] = useState([]);

  const [numeroFactura, setNumeroFactura] = useState('');
  const [tasa, setTasa] = useState('1');
  const [valorTotalDoc, setValorTotalDoc] = useState('');
  const [formaPagoDoc, setFormaPagoDoc] = useState('Contado');
  const [formasPagoDb, setFormasPagoDb] = useState([]);
  const [cargandoFormas, setCargandoFormas] = useState(false);
  const [formaPago, setFormaPago] = useState('');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [excentoItbis, setExcentoItbis] = useState('No');
  const [descItbis, setDescItbis] = useState('');
  const [descripcionGeneral, setDescripcionGeneral] = useState('');

  const [dialogAvancesOpen, setDialogAvancesOpen] = useState(false);
  const [avancesSuplidorLista, setAvancesSuplidorLista] = useState([]);
  const [cargandoAvancesSupl, setCargandoAvancesSupl] = useState(false);
  const [avanceIdsSeleccionados, setAvanceIdsSeleccionados] = useState([]);

  const [dialogNotasCreditoOpen, setDialogNotasCreditoOpen] = useState(false);
  const [notasCreditoSuplidorLista, setNotasCreditoSuplidorLista] = useState([]);
  const [cargandoNotasCreditoSupl, setCargandoNotasCreditoSupl] = useState(false);
  const [notaCreditoIdsSeleccionados, setNotaCreditoIdsSeleccionados] = useState([]);
  const [dialogNcfOpen, setDialogNcfOpen] = useState(false);
  const [dialog606Open, setDialog606Open] = useState(false);
  const [ncfData, setNcfData] = useState({
    rnc: '',
    tipo_id: '1-Rnc',
    tipo_servicio: '02-GASTOS POR TRABAJOS SUMINISTROS Y SERVICIOS',
    ncf: '',
    fecha_ncf: fechaLocalHoy(),
    tipo_bien: '01-Servicios',
    tipo_retencion: '00-None',
    monto_retencion: '',
    otros_impuestos: '',
    propina_legal: '',
    forma_pago_fiscal: '00-NONE',
  });

  const cuentasPosteables = useMemo(
    () => cuentasPosteablesDeCatalogo(cuentasCatalogo),
    [cuentasCatalogo]
  );

  const cargarCatalogoCuentas = useCallback(async () => {
    if (!usaCuentasContables || !puedeOperarDb) {
      setCuentasCatalogo([]);
      return;
    }
    setCargandoCatalogo(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcatalogoactualizable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda: '',
          orden: 'cuenta',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar el catálogo de cuentas');
        setCuentasCatalogo([]);
        return;
      }
      setCuentasCatalogo(data.cuentas || []);
    } catch {
      showError('Error de conexión al cargar el catálogo');
      setCuentasCatalogo([]);
    } finally {
      setCargandoCatalogo(false);
    }
  }, [usaCuentasContables, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarCentrosCosto = useCallback(async () => {
    if (!puedeOperarDb || !selectedCompany?.db_empresa) return;
    setCargandoCentros(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcentrodecosto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudieron cargar las localidades');
        setCentrosCosto([]);
        return;
      }
      const lista = data.centros || [];
      setCentrosCosto(
        lista.map((r) => ({
          id: r.id,
          descrip: (r.descrip || '').trim(),
        }))
      );
    } catch {
      showError('Error de conexión al cargar localidades');
      setCentrosCosto([]);
    } finally {
      setCargandoCentros(false);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarFormasPago = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) {
      setFormasPagoDb([]);
      return;
    }
    setCargandoFormas(true);
    try {
      const response = await fetch(`${servidor}/configuracionbackend_listarformasdepago`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setFormasPagoDb(data.formas_pago || []);
      } else {
        setFormasPagoDb([]);
        if (data.Error) showError(data.Error);
      }
    } catch {
      setFormasPagoDb([]);
    } finally {
      setCargandoFormas(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (formasPagoDb.length === 0) {
      setFormaPago('');
      return;
    }
    setFormaPago((prev) => {
      if (prev && formasPagoDb.some((r) => (r.documento || '') === prev)) return prev;
      return formasPagoDb[0].documento || '';
    });
  }, [formasPagoDb]);

  useEffect(() => {
    setCentroLineaSel((prev) =>
      prev && centrosCosto.some((x) => String(x.id) === String(prev.id)) ? prev : null
    );
  }, [centrosCosto]);

  const etiquetaLocalidadLinea = useCallback(
    (line) => {
      const id = (line.localidad || '').trim();
      if (!id) return '—';
      const c = centrosCosto.find((x) => String(x.id) === String(id));
      return c ? labelCentroCosto(c) : id;
    },
    [centrosCosto]
  );

  const filaFormaPagoActual = useMemo(
    () => formasPagoDb.find((r) => (r.documento || '') === formaPago),
    [formasPagoDb, formaPago]
  );

  const requiereReferencia =
    Boolean(filaFormaPagoActual) &&
    String(filaFormaPagoActual.necesita_referencia || 'N').toUpperCase() === 'S'
    && formaPagoDoc == 'Contado' ? true : false;

  useEffect(() => {
    if (!requiereReferencia) {
      setReferenciaPago('');
    }
  }, [requiereReferencia,formaPagoDoc]);

  const wanomesDesdeLogin = useMemo(
    () => wanomesDesdeAnomesContabilidad(selectedCompany?.anomescontabilidad),
    [selectedCompany?.anomescontabilidad]
  );

  const cargarVarinicialCont = useCallback(async () => {
    if (!puedeOperarDb) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_obtenervarinicialcont`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.wanomes) {
        setWanomesContableAbierto(String(data.wanomes));
      } else {
        if (!wanomesDesdeLogin) {
          setWanomesContableAbierto(null);
        }
        if (data.Error) {
          showWarning(data.Error);
        }
      }
    } catch {
      if (!wanomesDesdeLogin) {
        setWanomesContableAbierto(null);
      }
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, wanomesDesdeLogin, showWarning]);

  useEffect(() => {
    if (!open) return;
    if (wanomesDesdeLogin) {
      setWanomesContableAbierto(wanomesDesdeLogin);
      return;
    }
    setWanomesContableAbierto(null);
    if (puedeLeer && puedeOperarDb) {
      cargarVarinicialCont();
    }
  }, [
    open,
    wanomesDesdeLogin,
    selectedCompany?.db_empresa,
    puedeLeer,
    puedeOperarDb,
    cargarVarinicialCont,
  ]);

  const validacionFechaTransaccion = useMemo(
    () => validarMesContableAbierto(fechaTransaccion, wanomesContableAbierto),
    [fechaTransaccion, wanomesContableAbierto]
  );

  const textoPeriodoAbierto = useMemo(() => {
    if (!wanomesContableAbierto || wanomesContableAbierto.length !== 6) return null;
    const y = wanomesContableAbierto.slice(0, 4);
    const m = wanomesContableAbierto.slice(4, 6);
    return `${y}-${m} (año-mes mínimo permitido)`;
  }, [wanomesContableAbierto]);

  const cargarSuplidores = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      showError('Error de conexión al cargar suplidores');
      setSuplidores([]);
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const fetchCxP = useCallback(async () => {
      if (!puedeLeer || !puedeOperarDb) return;
      if (!suplidorSel?.id) {
        setFilas([]);
        setFechaCorteAntiguedad(null);
        return;
      }
      setCargando(true);
      try {
        const body = {
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          suplidor_id: String(suplidorSel.id),
        };
        const response = await fetch(`${servidor}/contabilidadbackend_listarcuentasxpagar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          const lista = data.facturas || [];
          setFilas(lista);
          setFechaCorteAntiguedad(data.fecha_corte_antiguedad || null);
          if (lista.length === 0) {
            showSuccess('No hay saldos pendientes de CXP para este suplidor');
          }
        } else {
          showError(data.Error || 'Error al cargar cuentas por pagar');
          setFilas([]);
          setFechaCorteAntiguedad(null);
        }
      } catch {
        showError('Error de conexión al cargar cuentas por pagar');
        setFilas([]);
        setFechaCorteAntiguedad(null);
      } finally {
        setCargando(false);
      }
    }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, suplidorSel, showError, showSuccess]);

  /** Pendiente de aplicar a factura: nfact vacío en BD (incl. 0 numérico por compatibilidad). */
  const avanceEsPendiente = useCallback((nfact) => {
    if (nfact == null) return true;
    if (typeof nfact === 'number' && nfact === 0) return true;
    return String(nfact).trim().length === 0;
  }, []);

  const avancesPendientesAplicar = useMemo(
    () => (avancesSuplidorLista || []).filter((a) => avanceEsPendiente(a.nfact)),
    [avancesSuplidorLista, avanceEsPendiente]
  );

  const sumaAvancesSeleccionados = useMemo(() => {
    const ids = new Set(avanceIdsSeleccionados.map(String));
    return avancesPendientesAplicar
      .filter((a) => ids.has(String(a.id)))
      .reduce((s, a) => s + (parseFloat(a.valor) || 0), 0);
  }, [avanceIdsSeleccionados, avancesPendientesAplicar]);

  const notaCreditoEsPendiente = useCallback((row) => {
    return String(row?.status || '').toUpperCase() !== 'S';
  }, []);

  const notasCreditoPendientesAplicar = useMemo(
    () => (notasCreditoSuplidorLista || []).filter((n) => notaCreditoEsPendiente(n)),
    [notasCreditoSuplidorLista, notaCreditoEsPendiente]
  );

  const montoNotaCreditoSuplidor = useCallback((row) => {
    const p = parseFloat(row?.precio) || 0;
    const it = parseFloat(row?.itbis) || 0;
    return p + it;
  }, []);

  const sumaNotasCreditoSeleccionados = useMemo(() => {
    const ids = new Set(notaCreditoIdsSeleccionados.map(String));
    return notasCreditoPendientesAplicar
      .filter((n) => ids.has(String(n.id)))
      .reduce((s, n) => s + montoNotaCreditoSuplidor(n), 0);
  }, [notaCreditoIdsSeleccionados, notasCreditoPendientesAplicar, montoNotaCreditoSuplidor]);

  const sumaAvancesYNotasCredito = useMemo(
    () => redondearDinero2(sumaAvancesSeleccionados + sumaNotasCreditoSeleccionados),
    [sumaAvancesSeleccionados, sumaNotasCreditoSeleccionados]
  );

  const cargarAvancesSuplidorDialog = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb || !suplidorSel?.id) return;
    setCargandoAvancesSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listaravancesuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          suplidor_id: String(suplidorSel.id),
          limite: 200,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setAvancesSuplidorLista(data.avances || []);
      } else {
        setAvancesSuplidorLista([]);
        showError(data.Error || 'No se pudieron cargar los avances del suplidor');
      }
    } catch {
      setAvancesSuplidorLista([]);
      showError('Error de conexión al cargar avances');
    } finally {
      setCargandoAvancesSupl(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, suplidorSel, showError]);

  const cargarNotasCreditoSuplidorDialog = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb || !suplidorSel?.id) return;
    setCargandoNotasCreditoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarnotacreditosuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          suplidor_id: String(suplidorSel.id),
          limite: 200,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setNotasCreditoSuplidorLista(data.notas || []);
      } else {
        setNotasCreditoSuplidorLista([]);
        showError(data.Error || 'No se pudieron cargar las notas de crédito del suplidor');
      }
    } catch {
      setNotasCreditoSuplidorLista([]);
      showError('Error de conexión al cargar notas de crédito');
    } finally {
      setCargandoNotasCreditoSupl(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, suplidorSel, showError]);

  const abrirDialogAvances = useCallback(() => {
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor para ver sus avances');
      return;
    }
    setDialogAvancesOpen(true);
    cargarAvancesSuplidorDialog();
  }, [suplidorSel, showError, cargarAvancesSuplidorDialog]);

  const abrirDialogNotasCredito = useCallback(() => {
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor para ver sus notas de crédito');
      return;
    }
    setDialogNotasCreditoOpen(true);
    cargarNotasCreditoSuplidorDialog();
  }, [suplidorSel, showError, cargarNotasCreditoSuplidorDialog]);

  const toggleSeleccionAvance = useCallback(
    (row) => {
      if (row == null || row.id == null) return;
      const idStr = String(row.id);
      const rawVt = (valorTotalDoc || '').trim().replace(',', '.');
      const vt = parseFloat(rawVt);
      setAvanceIdsSeleccionados((prev) => {
        const ya = prev.includes(idStr);
        const nextIds = ya ? prev.filter((x) => x !== idStr) : [...prev, idStr];
        const idsSet = new Set(nextIds.map(String));
        const sumaAv = avancesPendientesAplicar
          .filter((a) => idsSet.has(String(a.id)))
          .reduce((s, a) => s + (parseFloat(a.valor) || 0), 0);
        const idsNotas = new Set(notaCreditoIdsSeleccionados.map(String));
        const sumaNc = notasCreditoPendientesAplicar
          .filter((n) => idsNotas.has(String(n.id)))
          .reduce((s, n) => s + montoNotaCreditoSuplidor(n), 0);
        const suma = redondearDinero2(sumaAv + sumaNc);
        if (!ya && Number.isFinite(vt) && vt > 0 && suma > redondearDinero2(vt)) {
          showError(
            `La suma de avances y notas de crédito (${fmtMoney(suma)}) no puede ser mayor que el valor total de la factura (${fmtMoney(vt)}).`
          );
          return prev;
        }
        return nextIds;
      });
    },
    [
      valorTotalDoc,
      avancesPendientesAplicar,
      notaCreditoIdsSeleccionados,
      notasCreditoPendientesAplicar,
      montoNotaCreditoSuplidor,
      showError,
    ]
  );

  const toggleSeleccionNotaCredito = useCallback(
    (row) => {
      if (row == null || row.id == null) return;
      const idStr = String(row.id);
      const rawVt = (valorTotalDoc || '').trim().replace(',', '.');
      const vt = parseFloat(rawVt);
      setNotaCreditoIdsSeleccionados((prev) => {
        const ya = prev.includes(idStr);
        const nextIds = ya ? prev.filter((x) => x !== idStr) : [...prev, idStr];
        const idsSet = new Set(nextIds.map(String));
        const sumaNc = notasCreditoPendientesAplicar
          .filter((n) => idsSet.has(String(n.id)))
          .reduce((s, n) => s + montoNotaCreditoSuplidor(n), 0);
        const idsAv = new Set(avanceIdsSeleccionados.map(String));
        const sumaAv = avancesPendientesAplicar
          .filter((a) => idsAv.has(String(a.id)))
          .reduce((s, a) => s + (parseFloat(a.valor) || 0), 0);
        const suma = redondearDinero2(sumaAv + sumaNc);
        if (!ya && Number.isFinite(vt) && vt > 0 && suma > redondearDinero2(vt)) {
          showError(
            `La suma de avances y notas de crédito (${fmtMoney(suma)}) no puede ser mayor que el valor total de la factura (${fmtMoney(vt)}).`
          );
          return prev;
        }
        return nextIds;
      });
    },
    [
      valorTotalDoc,
      notasCreditoPendientesAplicar,
      montoNotaCreditoSuplidor,
      avanceIdsSeleccionados,
      avancesPendientesAplicar,
      showError,
    ]
  );

  useEffect(() => {
    setAvanceIdsSeleccionados([]);
    setAvancesSuplidorLista([]);
    setNotaCreditoIdsSeleccionados([]);
    setNotasCreditoSuplidorLista([]);
  }, [suplidorSel?.id]);

  useEffect(() => {
    const rawVt = (valorTotalDoc || '').trim().replace(',', '.');
    const vt = parseFloat(rawVt);
    if (!Number.isFinite(vt) || vt <= 0) return;
    if (avanceIdsSeleccionados.length === 0 && notaCreditoIdsSeleccionados.length === 0) return;
    if (redondearDinero2(sumaAvancesYNotasCredito) > redondearDinero2(vt)) {
      setAvanceIdsSeleccionados([]);
      setNotaCreditoIdsSeleccionados([]);
      showWarning(
        `Se anularon avances y notas de crédito seleccionadas: la suma (${fmtMoney(sumaAvancesYNotasCredito)}) superaba el nuevo valor total (${fmtMoney(vt)}).`
      );
    }
  }, [
    valorTotalDoc,
    avanceIdsSeleccionados,
    notaCreditoIdsSeleccionados,
    sumaAvancesYNotasCredito,
    showWarning,
  ]);

  useEffect(() => {
    if (!open || !puedeLeer || !puedeOperarDb) return;
    cargarSuplidores();
    cargarCatalogoCuentas();
    cargarCentrosCosto();
    cargarFormasPago();
  }, [
    open,
    puedeLeer,
    puedeOperarDb,
    selectedCompany?.db_empresa,
    cargarSuplidores,
    cargarCatalogoCuentas,
    cargarCentrosCosto,
    cargarFormasPago,
  ]);

  useEffect(() => {
    if (!open) return;
    if (suplidorSel?.id) {
      fetchCxP();
    } else {
      setFilas([]);
      setFechaCorteAntiguedad(null);
    }
  }, [open, suplidorSel, fetchCxP]);

  useEffect(() => {
    setNcfData((prev) => ({
      ...prev,
      rnc: (suplidorSel?.rnc || '').trim(),
    }));
  }, [suplidorSel?.rnc]);

  const abrirDialogNcf = useCallback(() => {
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor para registrar NCF');
      return;
    }
    if (!String(suplidorSel?.rnc || '').trim()) {
      showError('No hay RNC definido para el suplidor');
      return;
    }
    if (lineasDistribucion.length === 0) {
      showError('No hay gastos/compras en la distribución para asociar NCF');
      return;
    }
    setDialogNcfOpen(true);
  }, [suplidorSel, lineasDistribucion.length, showError]);

  const guardarNcfDialog = useCallback(() => {
    const rnc = (ncfData.rnc || '').trim();
    const ncf = (ncfData.ncf || '').trim().toUpperCase();
    if (!rnc) {
      showError('NCF: el RNC no puede estar en blanco');
      return;
    }
    if (!ncf) {
      showError('NCF: el número NCF no puede estar en blanco');
      return;
    }
    if (!/^[BE][013][1234][0-9]{10}$/.test(ncf)) {
      showError('NCF inválido. Formato esperado: B/E + 13x + 10 dígitos');
      return;
    }
    if ((ncfData.tipo_bien || '').startsWith('00')) {
      showError('NCF: seleccione tipo de bien/servicio');
      return;
    }
    if ((ncfData.tipo_id || '').startsWith('00')) {
      showError('NCF: seleccione tipo de ID');
      return;
    }
    if ((ncfData.forma_pago_fiscal || '').startsWith('00')) {
      showError('NCF: seleccione forma de pago fiscal');
      return;
    }
    setNcfData((prev) => ({ ...prev, ncf }));
    setDialogNcfOpen(false);
    showSuccess('NCF fiscal preparado para guardar con la factura');
  }, [ncfData, showError, showSuccess]);

  const enviarAlMotorContableCxp = useCallback(
    async (ctxDocumento) => {
      if (!moduloContabilidadInstalado(modulos)) return;
      if (!selectedCompany?.db_empresa) return;
      try {
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || 'No se pudieron consultar las reglas del motor contable');
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, TIPO_DOCUMENTO_MOTOR_CXP);
        if (!documentoMotorDefinido(reglasDoc, TIPO_DOCUMENTO_MOTOR_CXP)) {
          showWarning('No hay reglas del motor para Cuentas por Pagar; no se registró en diario/histdia');
          return;
        }
        const payloadDocumento = construirDocumentoMotorCxp(reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || 'No se pudo enviar el documento al motor contable');
          return;
        }
        if (motorData.omitido) return;
        if (motorData.ok) {
          showSuccess(motorData.mensaje || 'Motor contable: asiento generado');
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning('Error de conexión al enviar al motor contable');
      }
    },
    [modulos, servidor, selectedCompany, usuarioLog, claveLog, showWarning, showSuccess]
  );

  const handleGuardarCxp = useCallback(async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    if (!selectedCompany?.db_empresa) {
      showError('Servidor o empresa no disponible');
      return;
    }
    const vFecha = validarMesContableAbierto(fechaTransaccion, wanomesContableAbierto);
    if (!vFecha.ok) {
      showError(vFecha.message);
      return;
    }
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor');
      return;
    }
    const nf = (numeroFactura || '').trim();
    if (!nf) {
      showError('Indique el número de factura');
      return;
    }
    const rawValor = (valorTotalDoc || '').trim().replace(',', '.');
    const valorTotal = parseFloat(rawValor);
    if (!Number.isFinite(valorTotal) || valorTotal <= 0) {
      showError('Indique un valor total válido mayor que cero');
      return;
    }
    if (!FORMAS_PAGO_DOCUMENTO.includes(formaPagoDoc)) {
      showError('Forma del documento inválida (Contado / Credito)');
      return;
    }
    if (formaPagoDoc === 'Contado') {
      if (!formaPago || formasPagoDb.length === 0) {
        showError('Configure y seleccione una forma de pago');
        return;
      }
      if (requiereReferencia && !(referenciaPago || '').trim()) {
        showError('La forma de pago exige referencia');
        return;
      }
    }
    if (lineasDistribucion.length === 0) {
      showError('Agregue al menos una línea de distribución');
      return;
    }
    const sumaLineas = lineasDistribucion.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
    const sumaAvancesAplicar = avanceIdsSeleccionados.reduce((s, idStr) => {
      const row = avancesSuplidorLista.find((a) => String(a.id) === String(idStr));
      if (!row) return s;
      if (!avanceEsPendiente(row.nfact)) return s;
      return s + (parseFloat(row.valor) || 0);
    }, 0);
    const sumaNotasCreditoAplicar = notaCreditoIdsSeleccionados.reduce((s, idStr) => {
      const row = notasCreditoSuplidorLista.find((n) => String(n.id) === String(idStr));
      if (!row) return s;
      if (String(row.status || '').toUpperCase() === 'S') return s;
      return s + montoNotaCreditoSuplidor(row);
    }, 0);
    const totalDescuentosAplicar = redondearDinero2(sumaAvancesAplicar + sumaNotasCreditoAplicar);
    const totalNetoDistribucion = redondearDinero2(sumaLineas - totalDescuentosAplicar);
    if (Math.abs(totalNetoDistribucion - redondearDinero2(valorTotal)) > 0.02) {
      showError(
        `El total de la distribución menos avances y notas de crédito debe coincidir con el valor total de la factura. Neto distribución: $${fmtMoney(totalNetoDistribucion)} · Factura: $${fmtMoney(valorTotal)}.`
      );
      return;
    }
    setGuardandoCxp(true);
    try {
      const tasaNum = parseFloat(String(tasa || '').replace(',', '.'));
      const body = {
        id_empresa: selectedCompany,
        suplidor_id: String(suplidorSel.id),
        nfactura: nf,
        fecha: fechaTransaccion,
        tipodepago: formaPagoDoc,
        valor_total: valorTotal,
        tasa: Number.isFinite(tasaNum) ? tasaNum : undefined,
        forma_pago_documento: formaPagoDoc === 'Contado' ? formaPago : '',
        referencia_pago: (referenciaPago || '').trim(),
        excento_itbis: excentoItbis,
        desc_itbis: descItbis,
        descripcion_general: (descripcionGeneral || '').trim(),
        ncf_data: (ncfData?.ncf || '').trim()
          ? {
              rnc: (ncfData.rnc || '').trim(),
              tipo_id: ncfData.tipo_id || '',
              tipo_servicio: ncfData.tipo_servicio || '',
              ncf: (ncfData.ncf || '').trim().toUpperCase(),
              fecha_ncf: ncfData.fecha_ncf || '',
              tipo_bien: ncfData.tipo_bien || '',
              tipo_retencion: ncfData.tipo_retencion || '',
              monto_retencion: ncfData.monto_retencion || 0,
              otros_impuestos: ncfData.otros_impuestos || 0,
              propina_legal: ncfData.propina_legal || 0,
              forma_pago_fiscal: ncfData.forma_pago_fiscal || '',
            }
          : null,
        lineas_distribucion: lineasDistribucion.map((ln) => ({
          cuenta: usaCuentasContables ? ln.cuenta : '',
          descripcion: ln.descripcion,
          localidad: ln.localidad,
          valor: ln.valor,
        })),
        avances_aplicados_ids: avanceIdsSeleccionados
          .map((x) => parseInt(String(x), 10))
          .filter((n) => Number.isFinite(n)),
        notas_credito_aplicados_ids: notaCreditoIdsSeleccionados
          .map((x) => parseInt(String(x), 10))
          .filter((n) => Number.isFinite(n)),
        usuario: usuarioLog || '',
        password: claveLog || '',
        modulos,
      };
      const response = await fetch(`${servidor}/contabilidadbackend_guardarcuentasxpagar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo guardar la factura en CXP');
        return;
      }
      showSuccess(data.mensaje || 'Factura guardada en cuentas por pagar');
      const motorSave = data.motor;
      if (motorSave?.registrado) {
        showSuccess(motorSave.mensaje || 'Motor contable: asiento registrado en diario e histdia');
      } else if (motorSave && !motorSave.omitido && motorSave.mensaje) {
        showWarning(motorSave.mensaje);
      } else if (!motorSave?.registrado) {
        await enviarAlMotorContableCxp({
          valorTotal,
          lineas: lineasDistribucion,
          nfactura: nf,
          documento: data.documento || (formaPagoDoc === 'Credito' ? 'CUENTAS X PAGAR' : 'FACTURA GASTOS'),
          numero: data.numero,
          suplidor: String(suplidorSel.id),
          fecha: fechaTransaccion,
          tasa: Number.isFinite(tasaNum) ? tasaNum : 1,
          usuario: usuarioLog || '',
          concepto: (descripcionGeneral || '').trim() || `Factura ${nf}`,
        });
      }
      setNumeroFactura('');
      setValorTotalDoc('');
      setLineasDistribucion([]);
      setDescripcionGeneral('');
      setReferenciaPago('');
      setNcfData((prev) => ({ ...prev, ncf: '' }));
      setAvanceIdsSeleccionados([]);
      setNotaCreditoIdsSeleccionados([]);
      if (typeof onGuardado === 'function') onGuardado();
      if (typeof onClose === 'function') onClose();
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardandoCxp(false);
    }
  }, [
    puedeModificar,
    servidor,
    selectedCompany,
    fechaTransaccion,
    wanomesContableAbierto,
    suplidorSel,
    numeroFactura,
    valorTotalDoc,
    formaPagoDoc,
    formaPago,
    formasPagoDb.length,
    requiereReferencia,
    referenciaPago,
    lineasDistribucion,
    tasa,
    excentoItbis,
    descItbis,
    descripcionGeneral,
    ncfData,
    usaCuentasContables,
    modulos,
    usuarioLog,
    claveLog,
    showError,
    showSuccess,
    enviarAlMotorContableCxp,
    onGuardado,
    onClose,
    avanceIdsSeleccionados,
    avancesSuplidorLista,
    notaCreditoIdsSeleccionados,
    notasCreditoSuplidorLista,
    montoNotaCreditoSuplidor,
    avanceEsPendiente,
  ]);

  const handleNuevo = () => {
    setSuplidorSel(null);
    setCentroLineaSel(null);
    setFilas([]);
    setFechaCorteAntiguedad(null);
    setFechaTransaccion(fechaLocalHoy());
    setLineasDistribucion([]);
    setCuentaLineaSel(null);
    setValorLineaStr('');
    setNumeroFactura('');
    setTasa('1');
    setValorTotalDoc('');
    setFormaPagoDoc('Contado');
    setFormaPago('');
    setReferenciaPago('');
    setExcentoItbis('No');
    setDescItbis('');
    setDescripcionGeneral('');
    setAvanceIdsSeleccionados([]);
    setAvancesSuplidorLista([]);
    setDialogAvancesOpen(false);
    setNotaCreditoIdsSeleccionados([]);
    setNotasCreditoSuplidorLista([]);
    setDialogNotasCreditoOpen(false);
    setDialogNcfOpen(false);
    setNcfData({
      rnc: '',
      tipo_id: '1-Rnc',
      tipo_servicio: '02-GASTOS POR TRABAJOS SUMINISTROS Y SERVICIOS',
      ncf: '',
      fecha_ncf: fechaLocalHoy(),
      tipo_bien: '01-Servicios',
      tipo_retencion: '00-None',
      monto_retencion: '',
      otros_impuestos: '',
      propina_legal: '',
      forma_pago_fiscal: '00-NONE',
    });
  };

  useEffect(() => {
    if (open) {
      handleNuevo();
      return;
    }
    setDialogAvancesOpen(false);
    setDialogNotasCreditoOpen(false);
    setDialogNcfOpen(false);
    // Solo al abrir/cerrar el diálogo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const agregarLineaDistribucion = () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    const vFecha = validarMesContableAbierto(fechaTransaccion, wanomesContableAbierto);
    if (!vFecha.ok) {
      showError(vFecha.message);
      return;
    }
    if (formaPagoDoc === 'Contado') {
      if (!formaPago || formasPagoDb.length === 0) {
        showError('Configure y seleccione una forma de pago en Configuración (Gestión de formas de pago).');
        return;
      }
      if (requiereReferencia && !(referenciaPago || '').trim()) {
        showError('La forma de pago seleccionada exige referencia (cheque, transferencia, etc.).');
        return;
      }
    }
    let cuentaCod = '';
    let nombreCuenta = '';
    if (usaCuentasContables) {
      if (!cuentaLineaSel) {
        showError('Seleccione una cuenta del catálogo');
        return;
      }
      cuentaCod = String(cuentaLineaSel.cuenta ?? '').trim();
      nombreCuenta = String(cuentaLineaSel.nombre ?? '').trim();
    } else {
      nombreCuenta = (descripcionLineaStr || '').trim();
      if (!nombreCuenta) {
        showError('Indique la descripción de la línea');
        return;
      }
    }
    const centroId =
      centroLineaSel != null &&
      centroLineaSel.id != null &&
      String(centroLineaSel.id).trim() !== ''
        ? String(centroLineaSel.id).trim()
        : '';
    const raw = (valorLineaStr || '').trim().replace(',', '.');
    if (raw === '') {
      showError('Indique el valor de la línea');
      return;
    }
    const valorNum = parseFloat(raw);
    if (!Number.isFinite(valorNum)) {
      showError('Valor numérico inválido');
      return;
    }
    const idInterno = `${Date.now()}-${Math.floor(Math.random() * 1e15)}`;
    
    setLineasDistribucion((prev) => [
      ...prev,
      {
        key: idInterno,
        cuenta: cuentaCod,
        descripcion: nombreCuenta,
        localidad: centroId,
        valor: valorNum,
      },
    ]);
    setCuentaLineaSel(null);
    setDescripcionLineaStr('');
    setValorLineaStr('');
  };

  const eliminarLineaDistribucion = (key) => {
    if (!puedeModificar) return;
    setLineasDistribucion((prev) => prev.filter((r) => r.key !== key));
  };

  const totalDistribGasto = useMemo(
    () =>
      lineasDistribucion.filter((r) => r.valor > 0).reduce((s, r) => s + r.valor, 0),
    [lineasDistribucion]
  );
  const totalDistribDeduc = useMemo(
    () =>
      lineasDistribucion.filter((r) => r.valor < 0).reduce((s, r) => s + r.valor, 0),
    [lineasDistribucion]
  );
  /** Neto a factura: suma de líneas menos avances y N/C seleccionados (misma lógica que al guardar). */
  const totalDistribucion = useMemo(() => {
    const bruto = lineasDistribucion.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
    return redondearDinero2(bruto - sumaAvancesYNotasCredito);
  }, [lineasDistribucion, sumaAvancesYNotasCredito]);

  const ordenadasAntiguedad = useMemo(
    () => [...filas].sort((a, b) => (b.dias_antiguedad || 0) - (a.dias_antiguedad || 0)),
    [filas]
  );

  const tarjetas = useMemo(() => {
    const tramos = ['0-30', '31-60', '61-90', '90+'];
    return tramos.map((tramo) => {
      const enTramo = filas.filter((f) => f.tramo_antiguedad === tramo);
      const suma = enTramo.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0);
      return { tramo, cantidad: enTramo.length, suma };
    });
  }, [filas]);

  const totalCxp = useMemo(
    () => filas.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0),
    [filas]
  );

  const totalPagadoCxP = useMemo(() => filas.reduce((s, f) => s + pagadoCxPDesglose(f), 0), [filas]);

  const totalAvanceCxP = useMemo(
    () => filas.reduce((s, f) => s + (parseFloat(f.v_avance) || 0), 0),
    [filas]
  );

  const totalNcCxP = useMemo(
    () =>
      filas.reduce((s, f) => {
        const ncp = parseFloat(f.ncpagada);
        const vncDistrib = parseFloat(f.vnc) || 0;
        const vncSupl = parseFloat(f.v_notas_credito_suplidor) || 0;
        if (!Number.isNaN(ncp) && ncp > 0.005) return s + ncp;
        return s + vncDistrib + vncSupl;
      }, 0),
    [filas]
  );

  const nombreEmpresa =
    selectedCompany?.name ||
    selectedCompany?.empresa ||
    (typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '') ||
    'Empresa';

  const subtituloReporte = useMemo(() => {
    const sup = suplidorSel ? opcionLabel(suplidorSel) : 'Sin suplidor';
    const ft = fechaTransaccion ? `Fecha transacción ${formatearFecha(fechaTransaccion)}` : '';
    const corte =
      fechaCorteAntiguedad != null ? ` · Antigüedad al ${formatearFecha(fechaCorteAntiguedad)}` : '';
    return `${sup}${ft ? ` · ${ft}` : ''} · ${filas.length} documento(s)${corte}`;
  }, [suplidorSel, fechaTransaccion, fechaCorteAntiguedad, filas.length]);

  const columnasPrint = [
    { id: 'tramo_antiguedad', label: 'Tramo', align: 'center' },
    { id: 'dias_antiguedad', label: 'Días', align: 'center' },
    { id: 'nfactura', label: 'Nº factura' },
    { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
    { id: 'tipodefactura', label: 'Tipo pago' },
    { id: 'valor', label: 'Valor', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    {
      id: 'vplicado',
      label: 'Pagado (vplicado+ncpagada)',
      align: 'right',
      render: (_, row) => `$${fmtMoney(pagadoCxPDesglose(row))}`,
    },
    {
      id: 'saldo_pendiente',
      label: 'Saldo pendiente',
      align: 'right',
      render: (v) => `$${fmtMoney(v)}`,
    },
    { id: 'datafiscal', label: 'Datos fiscales', render: (v) => (v || '-').toString().slice(0, 80) },
  ];

  const accionNoImplementada = (nombre) => {
    showWarning(
      `${nombre}: la captura completa no está integrada en web aún. Use el módulo de escritorio (PyQt) o solicite ampliar esta pantalla.`
    );
  };

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const fpBtnContainedSx = {
    height: 46,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const fpBtnOutlinedSx = {
    height: 46,
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    bgcolor: '#FFFFFF',
    borderRadius: 1.6,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
    '&.Mui-disabled': { borderColor: FP.color.border, color: FP.color.textMuted },
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const cellEllipsisSx = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={guardandoCxp ? undefined : onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 1.5 },
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          overflow: 'hidden',
          m: { xs: 0, md: 1 },
          width: { xs: '100vw', md: 'calc(100vw - 16px)' },
          maxWidth: '100vw',
          height: { xs: '100vh', md: 'calc(100vh - 16px)' },
          maxHeight: { xs: '100vh', md: 'calc(100vh - 16px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.2, md: 2.6 },
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.4,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <CxpIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
            Registrar cuentas por pagar
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem', mt: 0.35 }}>
            {nombreNegocio
              ? `${nombreNegocio} · Facturas y cartera pendiente`
              : 'Facturas y cartera pendiente'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={guardandoCxp}
          aria-label="Cerrar"
          sx={{ color: FP.color.textSecondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 3 },
          bgcolor: FP.color.surface,
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 2, md: 2.5 },
          },
        }}
      >
        {!puedeLeer ? (
          <Alert severity="error">
            No tiene permiso para registrar cuentas por pagar.
          </Alert>
        ) : !puedeOperarDb ? (
          <Alert severity="info">
            Seleccione una empresa con base de datos asignada para administrar cuentas por pagar.
          </Alert>
        ) : (
      <div id="printable-cuentas-pagar">
        <Card elevation={0} sx={cardShellSx}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.2,
                    bgcolor: FP.color.accentBlueSoft,
                    color: FP.color.accentBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CxpIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
                    Registrar facturas
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                    Cartera pendiente por suplidor (factcxp — documento CUENTAS X PAGAR)
                    {fechaCorteAntiguedad != null
                      ? ` · Antigüedad al ${formatearFecha(fechaCorteAntiguedad)}`
                      : ''}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} className="no-print">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    cargarSuplidores();
                    cargarCatalogoCuentas();
                    cargarCentrosCosto();
                    cargarFormasPago();
                    cargarVarinicialCont();
                    if (suplidorSel?.id) fetchCxP();
                  }}
                  disabled={cargando || cargandoSupl}
                  sx={fpBtnOutlinedSx}
                >
                  Actualizar
                </Button>
                <PrintListado
                  title={nombreEmpresa}
                  reportTitle="Antigüedad de saldo — cuentas por pagar"
                  subtitle={subtituloReporte}
                  columns={columnasPrint}
                  data={ordenadasAntiguedad}
                  emptyMessage="Seleccione suplidor y cargue datos para imprimir"
                  printButtonLabel="Imprimir"
                  printButtonProps={{
                    disabled: filas.length === 0,
                    sx: fpBtnContainedSx,
                  }}
                  footer={`Total pendiente CXP: $${fmtMoney(totalCxp)} · Total pagado: $${fmtMoney(totalPagadoCxP)} · Total avance (dist.): $${fmtMoney(totalAvanceCxP)} · Total N/C (dist.): $${fmtMoney(totalNcCxP)}`}
                />
              </Box>
            </Box>

            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.8rem', fontWeight: 700, mb: 1, letterSpacing: '0.04em', textTransform: 'uppercase' }} className="no-print">
              Barra de acciones
            </Typography>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}
              className="no-print"
            >
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleNuevo}
                sx={fpBtnOutlinedSx}
              >
                Nuevo
              </Button>
              <Button
                variant="contained"
                startIcon={
                  guardandoCxp ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                }
                disabled={!puedeModificar || guardandoCxp || cargando}
                onClick={handleGuardarCxp}
                sx={fpBtnContainedSx}
              >
                Guardar
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }} className="no-print">
              <Button
                variant="outlined"
                startIcon={<NcfIcon />}
                disabled={!puedeModificar}
                onClick={abrirDialogNcf}
                sx={fpBtnOutlinedSx}
              >
                NCF
              </Button>
              <Button
                variant="outlined"
                startIcon={<Formato606Icon />}
                disabled={!puedeLeer}
                onClick={() => setDialog606Open(true)}
                sx={fpBtnOutlinedSx}
              >
                606
              </Button>
              <Button
                variant="outlined"
                startIcon={<AvancesIcon />}
                disabled={!puedeLeer || !suplidorSel?.id}
                onClick={abrirDialogAvances}
                sx={fpBtnOutlinedSx}
              >
                Avances del suplidor
              </Button>
              <Button
                variant="outlined"
                startIcon={<NotasCreditoSuplidorIcon />}
                disabled={!puedeLeer || !suplidorSel?.id}
                onClick={abrirDialogNotasCredito}
                sx={fpBtnOutlinedSx}
              >
                Notas de crédito del suplidor
              </Button>
              <Button
                variant="outlined"
                startIcon={<RecepcionIcon />}
                disabled={!puedeModificar}
                onClick={() => accionNoImplementada('Recepción de compras')}
                sx={fpBtnOutlinedSx}
              >
                RECEPCIÓN
              </Button>
              <Button
                variant="outlined"
                startIcon={<LiquidacionIcon />}
                disabled={!puedeModificar}
                onClick={() => accionNoImplementada('Liquidación')}
                sx={fpBtnOutlinedSx}
              >
                LIQUIDACIÓN
              </Button>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: FP.color.border }} className="no-print">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Datos del documento
              </Typography>
              {puedeLeer && puedeOperarDb && !cargandoFormas && formasPagoDb.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No hay formas de pago configuradas. Créelas en{' '}
                  <strong>Configuración → Gestión de Formas de Pago</strong> (documento = texto que se guardará en el
                  movimiento, p. ej. «Efectivo» o «Transferencia»).
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={5}>
                  <Autocomplete
                    options={suplidores}
                    loading={cargandoSupl}
                    getOptionLabel={(o) => opcionLabel(o)}
                    value={suplidorSel}
                    onChange={(_, v) => setSuplidorSel(v)}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Suplidor"
                        placeholder="id-nombre-rnc"
                        size="small"
                        helperText="Maestros tipo suplidor (maestcta)"
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    fullWidth
                    label="# Factura"
                    size="small"
                    value={numeroFactura}
                    onChange={(e) => setNumeroFactura(e.target.value)}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    fullWidth
                    label="Fecha"
                    type="date"
                    value={fechaTransaccion}
                    onChange={(e) => setFechaTransaccion(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    error={!validacionFechaTransaccion.ok}
                    helperText={
                      !validacionFechaTransaccion.ok
                        ? validacionFechaTransaccion.message
                        : textoPeriodoAbierto
                          ? `Mes abierto desde ${textoPeriodoAbierto} (varinicial).`
                          : 'Obteniendo período desde varinicial o sesión…'
                    }
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1}>
                  <TextField
                    fullWidth
                    label="Tasa"
                    size="small"
                    value={tasa}
                    onChange={(e) => setTasa(e.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={4} lg={2}>
                  <TextField
                    fullWidth
                    label="Valor total"
                    size="small"
                    value={valorTotalDoc}
                    onChange={(e) => setValorTotalDoc(e.target.value)}
                    placeholder="0.00"
                    inputProps={{ inputMode: 'decimal' }}
                    helperText={
                      avanceIdsSeleccionados.length > 0 || notaCreditoIdsSeleccionados.length > 0
                        ? `Avances + notas de crédito (suplidor): $${fmtMoney(sumaAvancesYNotasCredito)} (${avanceIdsSeleccionados.length + notaCreditoIdsSeleccionados.length} doc.) · no puede superar el valor total`
                        : ' '
                    }
                    FormHelperTextProps={{ sx: { m: 0, mt: 0.5 } }}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={fpInputSx}>
                    <InputLabel id="tipo-factura-doc-label">Tipo de Factura</InputLabel>
                    <Select
                      labelId="tipo-factura-doc-label"
                      label="Tipo de Factura"
                      value={formaPagoDoc}
                      onChange={(e) => setFormaPagoDoc(e.target.value)}
                    >
                      {FORMAS_PAGO_DOCUMENTO.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={formasPagoDb}
                    loading={cargandoFormas}
                    getOptionLabel={(o) => (o?.documento || '').trim() || '(sin nombre)'}
                    value={
                      formaPagoDoc === 'Credito'
                        ? null
                        : formasPagoDb.find((r) => (r.documento || '') === formaPago) || null
                    }
                    onChange={(_, v) => {
                      setFormaPago(v?.documento || '');
                      setReferenciaPago('');
                    }}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    disabled={
                      cargandoFormas || formasPagoDb.length === 0 || formaPagoDoc === 'Credito'
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Forma de Pago"
                        placeholder="Seleccione"
                        size="small"
                        helperText={
                          cargandoFormas
                            ? 'Cargando…'
                            : filaFormaPagoActual
                              ? requiereReferencia
                                ? 'Debe completar referencia'
                                : 'No requiere referencia'
                              : ' '
                        }
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Referencia al pago"
                    size="small"
                    value={requiereReferencia ? referenciaPago : ''}
                    onChange={(e) => setReferenciaPago(e.target.value)}
                    placeholder={requiereReferencia ? 'Nº cheque, transferencia, etc.' : ''}
                    disabled={!requiereReferencia}
                    required={requiereReferencia}
                    error={requiereReferencia && !(referenciaPago || '').trim()}
                    helperText={
                      requiereReferencia
                        ? 'Obligatorio: según configuración de la forma de pago'
                        : 'Deshabilitado: esta forma de pago no exige referencia'
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small" sx={fpInputSx}>
                    <InputLabel id="exento-itbis-label">Exento ITBIS</InputLabel>
                    <Select
                      labelId="exento-itbis-label"
                      label="Exento ITBIS"
                      value={excentoItbis}
                      onChange={(e) => setExcentoItbis(e.target.value)}
                    >
                      {OPCIONES_EXCENTO.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={8} md={2}>
                  <TextField
                    fullWidth
                    label="Desc. ITBIS"
                    size="small"
                    value={descItbis}
                    onChange={(e) => setDescItbis(e.target.value)}
                    placeholder="0.00"
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Descripción general de la factura"
                size="small"
                sx={{ mt: 2 }}
                value={descripcionGeneral}
                onChange={(e) => setDescripcionGeneral(e.target.value)}
                placeholder="Descripción general de la factura"
              />
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }} className="no-print">
              En el desglose, <strong>Pagado</strong> suma <strong>vplicado + ncpagada</strong> (efectivo/cheques y notas de
              crédito aplicadas). Saldo pendiente = valor − esa columna − avances (<strong>vavance</strong>). Seleccione un
              suplidor para cargar la cartera y el tablero por tramos 0‑30, 31‑60, 61‑90 y más de 90 días.
            </Alert>

            {!suplidorSel ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Seleccione un suplidor para cargar deudas pendientes.
              </Alert>
            ) : null}

            {suplidorSel ? (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {tarjetas.map(({ tramo, cantidad, suma }) => (
                    <Grid item xs={12} sm={6} md={3} key={tramo}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          height: '100%',
                          background:
                            tramo === '90+'
                              ? 'linear-gradient(135deg, rgba(211,47,47,0.08) 0%, rgba(255,255,255,1) 100%)'
                              : tramo === '61-90'
                                ? 'linear-gradient(135deg, rgba(237,108,2,0.08) 0%, rgba(255,255,255,1) 100%)'
                                : tramo === '31-60'
                                  ? 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 100%)'
                                  : 'linear-gradient(135deg, rgba(46,125,50,0.08) 0%, rgba(255,255,255,1) 100%)',
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {tramoLabel(tramo)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, my: 0.5 }}>
                          ${fmtMoney(suma)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cantidad} documento{cantidad !== 1 ? 's' : ''}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} className="no-print">
                  <Typography variant="subtitle2" color="text.secondary">
                    Total pendiente (suplidor seleccionado)
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    ${fmtMoney(totalCxp)}
                  </Typography>
                </Paper>
              </>
            ) : null}

            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : suplidorSel && filas.length === 0 ? (
              <Alert severity="info">No hay saldos pendientes para este suplidor.</Alert>
            ) : suplidorSel ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 900, sm: 1080 } }}>
                  <colgroup>
                    {['10%', '8%', '12%', '12%', '10%', '14%', '20%', '14%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Tramo
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Días
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Factura
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fecha
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Tipo
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Valor
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Pagado (vplicado+ncpagada)
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Saldo
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {ordenadasAntiguedad.map((row) => {
                      const tramoTxt = tramoLabel(row.tramo_antiguedad);
                      const tipoTxt = row.tipodefactura || '—';
                      return (
                        <TableRow key={`${row.nfactura}-${row.fecha}-${row.saldo_pendiente}`} hover>
                          <TableCell
                            align="center"
                            sx={cellEllipsisSx}
                            title={tramoTxt}
                          >
                            {tramoTxt}
                          </TableCell>
                          <TableCell align="center">{row.dias_antiguedad}</TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, color: FP.color.textPrimary, ...cellEllipsisSx }}
                            title={row.nfactura || '—'}
                          >
                            {row.nfactura || '—'}
                          </TableCell>
                          <TableCell align="center">{formatearFecha(row.fecha)}</TableCell>
                          <TableCell sx={cellEllipsisSx} title={tipoTxt}>
                            {tipoTxt}
                          </TableCell>
                          <TableCell align="right">${fmtMoney(row.valor)}</TableCell>
                          <TableCell align="right" sx={cellEllipsisSx} title={`$${fmtMoney(pagadoCxPDesglose(row))}`}>
                            ${fmtMoney(pagadoCxPDesglose(row))}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            ${fmtMoney(row.saldo_pendiente)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}

            <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 2 }} className="no-print">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Distribución del gasto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {usaCuentasContables
                  ? 'Cuenta, localidad opcional (sucursales y localidades existentes) y valor. La tabla muestra Cuenta, Descripción, Localidad, Valor; la última columna es Eliminar.'
                  : 'Descripción, localidad opcional (sucursales y localidades existentes) y valor. Las cuentas contables no están disponibles porque el módulo de contabilidad no está activo.'}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
                <Grid item xs={12} md={5}>
                  {usaCuentasContables ? (
                    <Autocomplete
                      options={cuentasPosteables}
                      loading={cargandoCatalogo}
                      value={cuentaLineaSel}
                      onChange={(_, v) => setCuentaLineaSel(v)}
                      getOptionLabel={(o) => labelCuentaCatalogo(o)}
                      isOptionEqualToValue={(a, b) => a?.id === b?.id}
                      filterOptions={(opts, state) => {
                        const q = (state.inputValue || '').toLowerCase().trim();
                        if (!q) return opts;
                        return opts.filter((c) => {
                          const cod = String(c.cuenta ?? '').toLowerCase();
                          const nom = String(c.nombre ?? '').toLowerCase();
                          return cod.includes(q) || nom.includes(q);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cuenta (catálogo)"
                          placeholder="Código o nombre"
                          size="small"
                          helperText="Cuentas del catálogo que admiten movimientos"
                        />
                      )}
                      disabled={!puedeModificar}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Descripción"
                      placeholder="Concepto del gasto o compra"
                      size="small"
                      value={descripcionLineaStr}
                      onChange={(e) => setDescripcionLineaStr(e.target.value)}
                      helperText="Sin módulo de contabilidad: indique el concepto, no una cuenta"
                      disabled={!puedeModificar}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={centrosCosto}
                    loading={cargandoCentros}
                    value={centroLineaSel}
                    onChange={(_, v) => setCentroLineaSel(v)}
                    getOptionLabel={(o) => labelCentroCosto(o)}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    filterOptions={(opts, state) => {
                      const q = (state.inputValue || '').toLowerCase().trim();
                      if (!q) return opts;
                      return opts.filter((c) => {
                        const id = String(c.id ?? '').toLowerCase();
                        const d = String(c.descrip ?? '').toLowerCase();
                        return id.includes(q) || d.includes(q);
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Localidad (línea, opcional)"
                        placeholder="Localidad o sucursal"
                        size="small"
                        helperText={
                          centrosCosto.length === 0
                            ? 'Sin localidades — puede dejar vacío'
                            : 'Opcional: sucursales y localidades existentes'
                        }
                      />
                    )}
                    disabled={!puedeModificar}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Valor"
                    size="small"
                    value={valorLineaStr}
                    onChange={(e) => setValorLineaStr(e.target.value)}
                    placeholder="0.00"
                    inputProps={{ inputMode: 'decimal' }}
                    disabled={!puedeModificar}
                  />
                </Grid>
                <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', pt: { md: 1 } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={agregarLineaDistribucion}
                    disabled={!puedeModificar || (usaCuentasContables && cargandoCatalogo)}
                    sx={{ ...fpBtnContainedSx, whiteSpace: 'nowrap' }}
                  >
                    Añadir
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Total gasto
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    ${fmtMoney(totalDistribGasto)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Total avances (selección)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                    ${fmtMoney(sumaAvancesSeleccionados)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Total notas de crédito (selección)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                    ${fmtMoney(sumaNotasCreditoSeleccionados)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total deducible
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                    ${fmtMoney(totalDistribDeduc)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total distribución (neto)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    ${fmtMoney(totalDistribucion)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    Líneas menos avances y N/C seleccionados
                  </Typography>
                </Grid>
              </Grid>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto', maxHeight: 320 }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 720, sm: 860 } }}>
                  <colgroup>
                    {(usaCuentasContables
                      ? ['16%', '34%', '26%', '14%', '10%']
                      : ['50%', '26%', '14%', '10%']
                    ).map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      {usaCuentasContables ? (
                        <TableCell>
                          <Typography component="span" sx={cellHeadSx}>
                            Cuenta
                          </Typography>
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Descripción
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Localidad
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Valor
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {lineasDistribucion.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={usaCuentasContables ? 5 : 4}>
                          <Typography variant="body2" color="text.secondary">
                            {usaCuentasContables
                              ? 'No hay líneas. Elija cuenta del catálogo, valor y opcionalmente localidad, luego Añadir.'
                              : 'No hay líneas. Indique descripción, valor y opcionalmente localidad, luego Añadir.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineasDistribucion.map((row) => {
                        const localidadTxt = etiquetaLocalidadLinea(row);
                        return (
                          <TableRow key={row.key} hover>
                            {usaCuentasContables ? (
                              <TableCell
                                sx={{ fontWeight: 700, color: FP.color.textPrimary, ...cellEllipsisSx }}
                                title={row.cuenta || '—'}
                              >
                                {row.cuenta || '—'}
                              </TableCell>
                            ) : null}
                            <TableCell sx={cellEllipsisSx} title={row.descripcion || '—'}>
                              {row.descripcion || '—'}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={localidadTxt}>
                              {localidadTxt}
                            </TableCell>
                            <TableCell align="right">${fmtMoney(row.valor)}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Eliminar línea">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => eliminarLineaDistribucion(row.key)}
                                    disabled={!puedeModificar}
                                    aria-label="Eliminar línea"
                                    sx={{
                                      color: FP.color.danger,
                                      '&:hover': { bgcolor: FP.color.dangerSoft },
                                      '&.Mui-disabled': { color: FP.color.textMuted },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </CardContent>
        </Card>
      </div>
        )}
      </DialogContent>
    </Dialog>

      <Formato606 abierto={dialog606Open} onCerrar={() => setDialog606Open(false)} />

      <Dialog
        open={dialogNcfOpen}
        onClose={() => setDialogNcfOpen(false)}
        maxWidth={false}
        fullWidth
        className="no-print"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 2.5 },
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
            m: { xs: 0, md: 2 },
            width: { xs: '100vw', md: 'min(96vw, 1280px)' },
            maxWidth: { xs: '100vw', md: '1280px' },
            maxHeight: { xs: '100vh', md: '92vh' },
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.7,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.4,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <NcfIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.02rem', lineHeight: 1.25 }}>
              Registrar NCF fiscal
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.76rem', mt: 0.2 }} noWrap>
              {suplidorSel ? opcionLabel(suplidorSel) : 'Datos del comprobante para el formato 606'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDialogNcfOpen(false)}
            aria-label="Cerrar"
            sx={{ color: FP.color.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
            bgcolor: FP.color.surface,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <NcfSeccion
            titulo="Identificación del suplidor"
            descripcion="RNC o cédula que se reportará en el comprobante fiscal"
          >
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  label="RNC / cédula"
                  value={ncfData.rnc}
                  onChange={(e) => setNcfData((p) => ({ ...p, rnc: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de identificación"
                  value={ncfData.tipo_id}
                  onChange={(e) => setNcfData((p) => ({ ...p, tipo_id: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {TIPOS_ID_NCF.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </NcfSeccion>

          <NcfSeccion
            titulo="Comprobante fiscal"
            descripcion="Número, fecha y forma de pago del e-NCF o NCF"
          >
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="NCF"
                  value={ncfData.ncf}
                  onChange={(e) => setNcfData((p) => ({ ...p, ncf: e.target.value.toUpperCase() }))}
                  helperText="Formato: B/E + 13x + 10 dígitos"
                  InputLabelProps={{ shrink: true }}
                  placeholder="E310000000001"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha NCF"
                  value={ncfData.fecha_ncf}
                  onChange={(e) => setNcfData((p) => ({ ...p, fecha_ncf: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Forma de pago fiscal"
                  value={ncfData.forma_pago_fiscal}
                  onChange={(e) => setNcfData((p) => ({ ...p, forma_pago_fiscal: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {FORMAS_PAGO_NCF.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </NcfSeccion>

          <NcfSeccion
            titulo="Clasificación 606"
            descripcion="Tipo de bien o servicio y categoría de gasto"
          >
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de bien"
                  value={ncfData.tipo_bien}
                  onChange={(e) => setNcfData((p) => ({ ...p, tipo_bien: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {TIPOS_BIEN_NCF.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de servicio"
                  value={ncfData.tipo_servicio}
                  onChange={(e) => setNcfData((p) => ({ ...p, tipo_servicio: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {TIPOS_SERVICIO_NCF.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </NcfSeccion>

          <NcfSeccion
            titulo="Retenciones e impuestos"
            descripcion="ISR retenido, otros impuestos y propina legal"
          >
            <Grid container spacing={2.25}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de retención"
                  value={ncfData.tipo_retencion}
                  onChange={(e) => setNcfData((p) => ({ ...p, tipo_retencion: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {TIPOS_RETENCION_NCF.map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Monto retención"
                  value={ncfData.monto_retencion}
                  onChange={(e) => setNcfData((p) => ({ ...p, monto_retencion: e.target.value }))}
                  inputProps={{ inputMode: 'decimal' }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Otros impuestos"
                  value={ncfData.otros_impuestos}
                  onChange={(e) => setNcfData((p) => ({ ...p, otros_impuestos: e.target.value }))}
                  inputProps={{ inputMode: 'decimal' }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Propina legal"
                  value={ncfData.propina_legal}
                  onChange={(e) => setNcfData((p) => ({ ...p, propina_legal: e.target.value }))}
                  inputProps={{ inputMode: 'decimal' }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </NcfSeccion>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button variant="outlined" onClick={() => setDialogNcfOpen(false)} sx={fpBtnOutlinedSx}>
            Cerrar
          </Button>
          <Button variant="contained" onClick={guardarNcfDialog} sx={fpBtnContainedSx}>
            Procesar NCF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogAvancesOpen}
        onClose={() => setDialogAvancesOpen(false)}
        maxWidth="md"
        fullWidth
        className="no-print"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Avances del suplidor
          {suplidorSel ? ` — ${opcionLabel(suplidorSel)}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Marque los avances pendientes (sin factura aplicada) que desea asociar a esta factura. Puede marcarlos antes
            de indicar el valor total; cuando el total esté definido, la suma de avances no podrá superarlo (tampoco al
            guardar).
          </Typography>
          {cargandoAvancesSupl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
            >
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 860, sm: 980 } }}>
                <colgroup>
                  {['8%', '10%', '12%', '14%', '20%', '18%', '18%'].map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        Usar
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Id
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        Fecha
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Valor
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Forma pago
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Referencia
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Factura aplicada
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {avancesSuplidorLista.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">
                          No hay avances registrados para este suplidor.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    avancesSuplidorLista.map((row) => {
                      const pendiente = avanceEsPendiente(row.nfact);
                      const idStr = String(row.id);
                      const marcado = avanceIdsSeleccionados.includes(idStr);
                      const fechaTxt = formatearFecha(row.fecha);
                      const formaPagoTxt = row.banco || '—';
                      const referenciaTxt = row.referencia || '—';
                      const facturaTxt = pendiente ? '—' : String(row.nfact || '—');
                      return (
                        <TableRow key={idStr} hover selected={marcado}>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={marcado}
                              disabled={!pendiente}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (pendiente) toggleSeleccionAvance(row);
                              }}
                              inputProps={{ 'aria-label': `Avance ${idStr}` }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, color: FP.color.textPrimary, ...cellEllipsisSx }}
                            title={idStr}
                          >
                            {idStr}
                          </TableCell>
                          <TableCell align="center" sx={cellEllipsisSx} title={fechaTxt}>
                            {fechaTxt}
                          </TableCell>
                          <TableCell align="right" sx={cellEllipsisSx} title={`$${fmtMoney(row.valor)}`}>
                            ${fmtMoney(row.valor)}
                          </TableCell>
                          <TableCell sx={cellEllipsisSx} title={formaPagoTxt}>
                            {formaPagoTxt}
                          </TableCell>
                          <TableCell sx={cellEllipsisSx} title={referenciaTxt}>
                            {referenciaTxt}
                          </TableCell>
                          <TableCell sx={cellEllipsisSx} title={facturaTxt}>
                            {facturaTxt}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Typography variant="subtitle2">
              Seleccionados: <strong>${fmtMoney(sumaAvancesSeleccionados)}</strong> ·{' '}
              {avanceIdsSeleccionados.length} documento{avanceIdsSeleccionados.length !== 1 ? 's' : ''}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setAvanceIdsSeleccionados([])}
              disabled={avanceIdsSeleccionados.length === 0}
              sx={fpBtnOutlinedSx}
            >
              Quitar selección
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDialogAvancesOpen(false)} sx={fpBtnOutlinedSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogNotasCreditoOpen}
        onClose={() => setDialogNotasCreditoOpen(false)}
        maxWidth="md"
        fullWidth
        className="no-print"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Notas de crédito del suplidor
          {suplidorSel ? ` — ${opcionLabel(suplidorSel)}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Marque las notas pendientes (sin aplicar a factura) que desea asociar a esta factura. El monto considerado
            es precio + ITBIS de cada nota. Junto con los avances, la suma no podrá superar el valor total al guardar.
          </Typography>
          {cargandoNotasCreditoSupl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" align="center">
                      Usar
                    </TableCell>
                    <TableCell>Id / ncred</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>NCF</TableCell>
                    <TableCell>Factura aplicada</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notasCreditoSuplidorLista.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No hay notas de crédito registradas para este suplidor.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notasCreditoSuplidorLista.map((row) => {
                      const pendiente = notaCreditoEsPendiente(row);
                      const idStr = String(row.id);
                      const marcado = notaCreditoIdsSeleccionados.includes(idStr);
                      return (
                        <TableRow key={idStr} hover selected={marcado}>
                          <TableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={marcado}
                              disabled={!pendiente}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (pendiente) toggleSeleccionNotaCredito(row);
                              }}
                              inputProps={{ 'aria-label': `Nota de crédito suplidor ${idStr}` }}
                            />
                          </TableCell>
                          <TableCell>
                            {idStr} / {row.ncred != null && String(row.ncred) !== '' ? String(row.ncred) : '—'}
                          </TableCell>
                          <TableCell>{formatearFecha(row.fecha)}</TableCell>
                          <TableCell align="right">${fmtMoney(montoNotaCreditoSuplidor(row))}</TableCell>
                          <TableCell>{row.ncf || '—'}</TableCell>
                          <TableCell>
                            {pendiente ? '—' : (row.nfactura_aplicada || '').trim() || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Typography variant="subtitle2">
              Seleccionadas: <strong>${fmtMoney(sumaNotasCreditoSeleccionados)}</strong> ·{' '}
              {notaCreditoIdsSeleccionados.length} documento{notaCreditoIdsSeleccionados.length !== 1 ? 's' : ''}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setNotaCreditoIdsSeleccionados([])}
              disabled={notaCreditoIdsSeleccionados.length === 0}
              sx={fpBtnOutlinedSx}
            >
              Quitar selección
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDialogNotasCreditoOpen(false)} sx={fpBtnOutlinedSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegistrarCuentasXPagar;
